import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Anchor, Copy, Check, RefreshCw, Loader2, Webhook, TrendingUp, AlertTriangle,
  Trash2, PlayCircle, ExternalLink, Building2, Users, Download, MailCheck, UserPlus,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/webhooks/fareharbor`;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const when = (value) => (value ? new Date(value).toLocaleString() : '\u2014');

const CopyField = ({ label, value, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      /* clipboard blocked - the field is selectable anyway */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-white/50">{label}</label>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={value}
          onFocus={(e) => e.target.select()}
          className="w-full rounded-lg border border-white/10 bg-[#061a1f] px-3 py-2 font-mono text-xs text-teal-200"
          data-testid={testId}
        />
        <button onClick={copy} className="rounded-lg border border-white/10 p-2 text-white/60 hover:text-white" title="Copy">
          {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

const Stat = ({ label, value, hint, tone = 'default', testId }) => (
  <div className={`rounded-xl border p-4 ${tone === 'good' ? 'border-emerald-400/30 bg-emerald-400/5' : 'border-white/10 bg-[#0b1f24]'}`} data-testid={testId}>
    <p className="text-2xl font-black">{value}</p>
    <p className="text-xs text-white/50">{label}</p>
    {hint && <p className="mt-1 text-[11px] text-white/35">{hint}</p>}
  </div>
);

/**
 * FareHarbor webhooks: the URLs to paste into FareHarbor, what has arrived, and what it is worth.
 *
 * FareHarbor cannot send a signature, so the secret is part of the URL - rotating it changes both
 * URLs. Everything on this page comes from the webhook payloads FareHarbor sends; nothing is
 * estimated except the margin column, which is receipt total minus the invoice price.
 */
const FareHarborWebhooks = () => {
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [itemChanges, setItemChanges] = useState([]);
  const [events, setEvents] = useState([]);
  const [guests, setGuests] = useState({ count: 0, opt_in_count: 0, guests: [] });
  const [guestFilters, setGuestFilters] = useState({ opt_in_only: false, company: '', activity: '', month: '', min_trips: 0, min_spend: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sum, b, i, e, g] = await Promise.all([
        axios.get(`${BASE}/admin/settings`, { headers: authHeaders() }),
        axios.get(`${BASE}/admin/summary`, { headers: authHeaders() }),
        axios.get(`${BASE}/admin/bookings`, { headers: authHeaders() }),
        axios.get(`${BASE}/admin/item-changes`, { headers: authHeaders() }),
        axios.get(`${BASE}/admin/events?limit=25`, { headers: authHeaders() }),
        axios.get(`${BASE}/admin/guests`, { headers: authHeaders(), params: guestFilters }),
      ]);
      setSettings(s.data);
      setSummary(sum.data);
      setBookings(b.data || []);
      setItemChanges(i.data || []);
      setEvents(e.data || []);
      setGuests(g.data || { count: 0, opt_in_count: 0, guests: [] });
    } catch (error) {
      toast({ title: 'Could not load webhook data', description: error.response?.data?.detail || error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [guestFilters]);

  useEffect(() => { load(); }, [load]);

  const downloadGuestCsv = () => {
    const params = new URLSearchParams({
      ...Object.fromEntries(Object.entries(guestFilters).map(([k, v]) => [k, String(v)])),
      include_samples: 'true',
      token: localStorage.getItem('token') || '',
    });
    window.open(`${BASE}/admin/guests.csv?${params.toString()}`, '_blank');
  };

  const rebuildGuests = async () => {
    setBusy('guests');
    try {
      const res = await axios.post(`${BASE}/admin/rebuild-guests`, {}, { headers: authHeaders() });
      toast({ title: 'Guest list rebuilt', description: `${res.data.guests} guest(s) from ${res.data.bookings} booking(s)` });
      await load();
    } catch (error) {
      toast({ title: 'Could not rebuild the guest list', variant: 'destructive' });
    } finally { setBusy(''); }
  };

  const selfTest = async () => {
    setBusy('self-test');
    try {
      const res = await axios.post(`${BASE}/admin/self-test`, {}, { headers: authHeaders() });
      toast({ title: 'Sample booking pushed through the receiver', description: res.data.uuid });
      await load();
    } catch (error) {
      toast({ title: 'Self test failed', description: error.response?.data?.detail || error.message, variant: 'destructive' });
    } finally { setBusy(''); }
  };

  const clearSamples = async () => {
    setBusy('clear');
    try {
      const res = await axios.delete(`${BASE}/admin/self-test`, { headers: authHeaders() });
      toast({ title: 'Test data cleared', description: `${res.data.bookings_removed} booking(s), ${res.data.events_removed} event(s)` });
      await load();
    } catch (error) {
      toast({ title: 'Could not clear test data', variant: 'destructive' });
    } finally { setBusy(''); }
  };

  const rotate = async () => {
    if (!window.confirm('Rotate the secret? The current URLs stop working immediately and must be re-pasted into FareHarbor.')) return;
    setBusy('rotate');
    try {
      await axios.post(`${BASE}/admin/rotate-secret`, {}, { headers: authHeaders() });
      toast({ title: 'New secret generated', description: 'Paste the new URLs into FareHarbor.' });
      await load();
    } catch (error) {
      toast({ title: 'Could not rotate the secret', variant: 'destructive' });
    } finally { setBusy(''); }
  };

  const urls = settings?.urls_for_configured_domain || settings?.urls_for_this_host || {};
  const totals = summary?.totals || {};
  const staleItems = itemChanges.filter((c) => !c.matches_activity);

  if (loading && !settings) {
    return (
      <div className="-m-4 flex min-h-screen items-center justify-center bg-[#061a1f] text-white lg:-m-6">
        <Loader2 className="h-7 w-7 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="-m-4 min-h-screen bg-[#061a1f] p-5 text-white lg:-m-6 lg:p-8" data-testid="fareharbor-webhooks-page">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-black"><Webhook className="h-6 w-6 text-teal-400" /> FareHarbor Webhooks</h1>
          <p className="text-sm text-white/40">
            Bookings, payments and item changes pushed to us by FareHarbor - no API key needed.
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:text-white" data-testid="fh-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {settings?.rejected_count > 0 && (
        <div className="mb-6 flex flex-wrap items-start gap-3 rounded-2xl border border-red-400/40 bg-red-500/10 p-4" data-testid="fh-rejected-banner">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-300" />
          <div className="min-w-0">
            <p className="font-bold text-red-200">
              {settings.rejected_count} delivery(ies) were rejected - the secret in the URL does not match this site
            </p>
            <p className="mt-1 text-sm text-red-200/80">
              Something is calling the webhook with the wrong secret. Almost always this means the URL was copied from a
              different environment (the preview build and the live site each have their own secret).
              <strong> Copy the URLs below</strong> - they are this site&rsquo;s - and paste them into FareHarbor again.
              Last rejected {when(settings.last_rejected_at)}.
            </p>
          </div>
        </div>
      )}

      {/* ---------- the URLs ---------- */}
      <div className="mb-6 rounded-2xl border border-white/10 bg-[#0b1f24] p-5" data-testid="fh-webhook-urls">
        <h2 className="mb-1 font-bold">1. Paste these into FareHarbor</h2>
        <p className="mb-4 text-xs text-white/40">
          FareHarbor dashboard &rarr; <strong>Webhooks</strong> tab (or email support@fareharbor.com). Start with the
          test URL, choose the <strong>Bookings + payments</strong> data type, and ask them to switch the
          <strong> item webhook</strong> on as well. The URL must be https with a valid certificate.
        </p>
        <div className="grid gap-3 lg:grid-cols-2">
          <CopyField label="Test webhook URL" value={urls.test || ''} testId="fh-url-test" />
          <CopyField label="Production webhook URL" value={urls.production || ''} testId="fh-url-production" />
        </div>
        {settings?.configured_domain && (
          <p className="mt-2 text-[11px] text-white/35">
            Built from the Site URL in settings ({settings.configured_domain}). That domain must serve this app before
            FareHarbor can reach it.
          </p>
        )}
        {settings?.urls_for_this_host?.production
          && settings.urls_for_this_host.production !== urls.production && (
          <details className="mt-3 rounded-lg border border-white/10 bg-[#061a1f] p-3" data-testid="fh-host-urls">
            <summary className="cursor-pointer text-xs font-semibold text-white/60">
              Use the URL of the domain this app is served from instead
            </summary>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              <CopyField label="Test (this host)" value={settings.urls_for_this_host.test} testId="fh-url-test-host" />
              <CopyField label="Production (this host)" value={settings.urls_for_this_host.production} testId="fh-url-production-host" />
            </div>
            <p className="mt-2 text-[11px] text-white/35">
              Pick whichever domain actually answers on the public internet - FareHarbor needs a reachable https URL.
            </p>
          </details>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button onClick={selfTest} disabled={busy === 'self-test'} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-2.5 text-sm font-semibold disabled:opacity-50" data-testid="fh-self-test">
            {busy === 'self-test' ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Send test booking through the receiver
          </button>
          <button onClick={clearSamples} disabled={busy === 'clear'} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:text-white disabled:opacity-50" data-testid="fh-clear-samples">
            <Trash2 className="h-4 w-4" /> Clear test data
          </button>
          <button onClick={rotate} disabled={busy === 'rotate'} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-amber-300/80 hover:text-amber-200 disabled:opacity-50" data-testid="fh-rotate-secret">
            <RefreshCw className="h-4 w-4" /> Rotate secret
          </button>
        </div>
        <div className="mt-4 grid gap-2 text-xs text-white/50 sm:grid-cols-2">
          <p data-testid="fh-last-test">Last <strong>test</strong> webhook received: {when(settings?.last_test_at)}</p>
          <p data-testid="fh-last-production">Last <strong>production</strong> webhook received: {when(settings?.last_production_at)}</p>
        </div>
      </div>

      {/* ---------- money ---------- */}
      <h2 className="mb-3 flex items-center gap-2 font-bold"><TrendingUp className="h-4 w-4 text-teal-400" /> 2. What has come through (last {summary?.days || 90} days)</h2>
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-6" data-testid="fh-totals">
        <Stat label="Bookings" value={totals.bookings || 0} testId="fh-total-bookings" />
        <Stat label="Guests" value={totals.guests || 0} />
        <Stat label="Guest paid (gross)" value={money(totals.gross)} />
        <Stat label="Operator invoices" value={money(totals.invoiced)} />
        <Stat label="Your margin" value={money(totals.margin)} tone="good" hint="receipt total &minus; invoice price" testId="fh-total-margin" />
        <Stat label="Refunded" value={money(totals.refunded)} hint={`${totals.cancelled || 0} cancelled`} />
      </div>

      {(summary?.by_company || []).length > 0 && (
        <div className="mb-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#0b1f24]" data-testid="fh-by-company">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-white/40">
              <tr>
                <th className="p-3">Charter company</th>
                <th className="p-3">Bookings</th>
                <th className="p-3">Guests</th>
                <th className="p-3">Gross</th>
                <th className="p-3">Invoiced</th>
                <th className="p-3">Margin</th>
                <th className="p-3">Refunded</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_company.map((row) => (
                <tr key={row.company_shortname} className="border-b border-white/5 last:border-0">
                  <td className="p-3 font-semibold"><Building2 className="mr-1.5 inline h-3.5 w-3.5 text-white/30" />{row.company_name}</td>
                  <td className="p-3">{row.bookings}</td>
                  <td className="p-3">{row.guests}</td>
                  <td className="p-3">{money(row.gross)}</td>
                  <td className="p-3">{money(row.invoiced)}</td>
                  <td className="p-3 font-bold text-emerald-300">{money(row.margin)}</td>
                  <td className="p-3 text-white/60">{money(row.refunded)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------- bookings ---------- */}
      <h2 className="mb-3 font-bold">3. Bookings received</h2>
      {bookings.length === 0 ? (
        <div className="mb-6 rounded-2xl border border-dashed border-white/10 bg-[#0b1f24] py-12 text-center text-white/40" data-testid="fh-no-bookings">
          Nothing yet. Paste the test URL into FareHarbor and make a test booking, or press
          &ldquo;Send test booking through the receiver&rdquo; above to prove the pipeline works.
        </div>
      ) : (
        <div className="mb-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#0b1f24]" data-testid="fh-bookings-table">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-white/40">
              <tr>
                <th className="p-3">Booked</th>
                <th className="p-3">Company / Activity</th>
                <th className="p-3">Guest</th>
                <th className="p-3">Party</th>
                <th className="p-3">Departs</th>
                <th className="p-3">Gross</th>
                <th className="p-3">Margin</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.uuid} className="border-b border-white/5 last:border-0" data-testid={`fh-booking-${b.uuid}`}>
                  <td className="p-3 whitespace-nowrap text-white/60">{when(b.booked_at)}</td>
                  <td className="p-3">
                    <span className="block font-semibold">{b.item_name || '\u2014'}</span>
                    <span className="text-xs text-white/40">{b.company_name} {b.is_sample && <em className="ml-1 rounded bg-amber-400/15 px-1.5 py-0.5 text-[10px] font-bold uppercase not-italic text-amber-300">test data</em>}</span>
                  </td>
                  <td className="p-3">
                    <span className="block">{b.contact_name || '\u2014'}</span>
                    <span className="text-xs text-white/40">{b.contact_email}</span>
                  </td>
                  <td className="p-3"><Users className="mr-1 inline h-3.5 w-3.5 text-white/30" />{b.customer_count ?? '\u2014'}</td>
                  <td className="p-3 whitespace-nowrap text-white/60">{when(b.starts_at)}</td>
                  <td className="p-3">{money(b.receipt_total)}</td>
                  <td className="p-3 font-semibold text-emerald-300">{b.margin === null || b.margin === undefined ? '\u2014' : money(b.margin)}</td>
                  <td className="p-3">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${
                      b.status === 'cancelled' ? 'bg-red-500/15 text-red-300'
                        : b.status === 'rebooked' ? 'bg-amber-400/15 text-amber-300'
                        : 'bg-emerald-500/15 text-emerald-300'}`}>
                      {b.status || 'unknown'}
                    </span>
                    <span className="mt-1 block text-[11px] text-white/30">{b.source_type} &middot; {b.environment}</span>
                  </td>
                  <td className="p-3">
                    {b.dashboard_url && (
                      <a href={b.dashboard_url} target="_blank" rel="noopener noreferrer" className="text-teal-300 hover:underline" title="Open in FareHarbor">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------- item changes ---------- */}
      <h2 className="mb-3 font-bold">4. Activity changes FareHarbor reported</h2>
      {itemChanges.length === 0 ? (
        <div className="mb-6 rounded-2xl border border-dashed border-white/10 bg-[#0b1f24] py-10 text-center text-sm text-white/40" data-testid="fh-no-item-changes">
          None yet. Ask FareHarbor support to switch the item webhook on - it tells us when an operator renames,
          re-prices or retires an activity.
        </div>
      ) : (
        <div className="mb-6 space-y-2" data-testid="fh-item-changes">
          {staleItems.length > 0 && (
            <p className="flex items-center gap-2 rounded-xl border border-amber-400/30 bg-amber-400/5 p-3 text-xs text-amber-300">
              <AlertTriangle className="h-4 w-4" /> {staleItems.length} change(s) are for an activity we do not carry under that
              FareHarbor handle - check the charter company&rsquo;s shortname or add the activity.
            </p>
          )}
          {itemChanges.map((c) => (
            <div key={c.reference} className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-[#0b1f24] p-3 text-sm">
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${c.matches_activity ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-400/15 text-amber-300'}`}>
                {c.matches_activity ? 'matched' : 'unmatched'}
              </span>
              <span className="font-semibold">{c.item_name}</span>
              <span className="text-white/40">{c.company_name} ({c.company_shortname}:{c.item_pk})</span>
              <span className="ml-auto text-xs text-white/40">{when(c.last_changed_at)} &middot; {c.change_count}&times;</span>
              {c.dashboard_url && (
                <a href={c.dashboard_url} target="_blank" rel="noopener noreferrer" className="text-teal-300 hover:underline"><ExternalLink className="h-4 w-4" /></a>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ---------- guests ---------- */}
      <h2 className="mb-1 flex items-center gap-2 font-bold"><UserPlus className="h-4 w-4 text-teal-400" /> 5. Guests &amp; remarketing</h2>
      <p className="mb-3 text-xs text-white/40">
        Built from the bookings above and kept in Contacts, de-duplicated by email. Waiver details
        (date of birth, home address) are never stored. <strong>Only guests who opted in with the
        operator should be emailed</strong> - the export defaults to those.
      </p>

      <div className="mb-3 flex flex-wrap items-end gap-2 rounded-2xl border border-white/10 bg-[#0b1f24] p-4" data-testid="fh-guest-filters">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={guestFilters.opt_in_only}
            onChange={(e) => setGuestFilters((f) => ({ ...f, opt_in_only: e.target.checked }))}
            data-testid="fh-guest-optin-only"
          />
          Opt-ins only
        </label>
        <input className="rounded-lg border border-white/10 bg-[#061a1f] px-3 py-2 text-sm" placeholder="Charter company" value={guestFilters.company} onChange={(e) => setGuestFilters((f) => ({ ...f, company: e.target.value }))} data-testid="fh-guest-company" />
        <input className="rounded-lg border border-white/10 bg-[#061a1f] px-3 py-2 text-sm" placeholder="Activity (exact tag)" value={guestFilters.activity} onChange={(e) => setGuestFilters((f) => ({ ...f, activity: e.target.value }))} data-testid="fh-guest-activity" />
        <input className="w-32 rounded-lg border border-white/10 bg-[#061a1f] px-3 py-2 text-sm" placeholder="Month YYYY-MM" value={guestFilters.month} onChange={(e) => setGuestFilters((f) => ({ ...f, month: e.target.value }))} data-testid="fh-guest-month" />
        <input type="number" min="0" className="w-28 rounded-lg border border-white/10 bg-[#061a1f] px-3 py-2 text-sm" placeholder="Min trips" value={guestFilters.min_trips} onChange={(e) => setGuestFilters((f) => ({ ...f, min_trips: Number(e.target.value) || 0 }))} data-testid="fh-guest-min-trips" />
        <input type="number" min="0" className="w-32 rounded-lg border border-white/10 bg-[#061a1f] px-3 py-2 text-sm" placeholder="Min spend" value={guestFilters.min_spend} onChange={(e) => setGuestFilters((f) => ({ ...f, min_spend: Number(e.target.value) || 0 }))} data-testid="fh-guest-min-spend" />
        <span className="ml-auto text-sm text-white/50" data-testid="fh-guest-count">
          {guests.count} guest(s) &middot; <MailCheck className="inline h-3.5 w-3.5 text-emerald-400" /> {guests.opt_in_count} opted in
        </span>
        <button onClick={downloadGuestCsv} className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 px-4 py-2.5 text-sm font-semibold" data-testid="fh-guest-csv">
          <Download className="h-4 w-4" /> Download CSV
        </button>
        <button onClick={rebuildGuests} disabled={busy === 'guests'} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-2.5 text-sm font-semibold text-white/70 hover:text-white disabled:opacity-50" data-testid="fh-guest-rebuild">
          <RefreshCw className="h-4 w-4" /> Rebuild
        </button>
      </div>

      {guests.guests.length === 0 ? (
        <div className="mb-6 rounded-2xl border border-dashed border-white/10 bg-[#0b1f24] py-10 text-center text-sm text-white/40" data-testid="fh-no-guests">
          No guests yet - they appear here automatically as bookings arrive.
        </div>
      ) : (
        <div className="mb-6 overflow-x-auto rounded-2xl border border-white/10 bg-[#0b1f24]" data-testid="fh-guests-table">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-white/40">
              <tr>
                <th className="p-3">Guest</th>
                <th className="p-3">Email / phone</th>
                <th className="p-3">Trips</th>
                <th className="p-3">Lifetime spend</th>
                <th className="p-3">Last trip</th>
                <th className="p-3">Marketing</th>
                <th className="p-3">Tags</th>
              </tr>
            </thead>
            <tbody>
              {guests.guests.map((g) => (
                <tr key={g.id || g.email} className="border-b border-white/5 last:border-0" data-testid={`fh-guest-${g.email || g.id}`}>
                  <td className="p-3 font-semibold">
                    {g.name}
                    {g.is_sample && <em className="ml-2 rounded bg-amber-400/15 px-1.5 py-0.5 text-[10px] font-bold uppercase not-italic text-amber-300">test data</em>}
                  </td>
                  <td className="p-3 text-white/60"><span className="block">{g.email}</span><span className="text-xs">{g.phone_number}</span></td>
                  <td className="p-3">{g.trip_count}</td>
                  <td className="p-3">{money(g.lifetime_spend)}</td>
                  <td className="p-3 whitespace-nowrap text-white/60">{g.last_trip_at ? when(g.last_trip_at) : '\u2014'}</td>
                  <td className="p-3">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${g.marketing_opt_in ? 'bg-emerald-500/15 text-emerald-300' : 'bg-white/10 text-white/40'}`}>
                      {g.marketing_opt_in ? 'opted in' : 'do not email'}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-white/40">{(g.tags || []).slice(0, 4).join(' \u00b7 ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------- raw log ---------- */}
      <h2 className="mb-3 font-bold">6. Recent deliveries</h2>
      <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#0b1f24]" data-testid="fh-events">
        {events.length === 0 ? (
          <p className="py-10 text-center text-sm text-white/40">No webhook deliveries recorded yet.</p>
        ) : (
          <ul className="divide-y divide-white/5 text-sm">
            {events.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-3 p-3">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                  e.kind === 'booking' ? 'bg-teal-500/15 text-teal-300'
                    : e.kind === 'item' ? 'bg-blue-500/15 text-blue-300'
                    : e.kind === 'crew_member' ? 'bg-purple-500/15 text-purple-300'
                    : 'bg-red-500/15 text-red-300'}`}>{e.kind}</span>
                <span className="font-mono text-xs text-white/60">{e.reference || e.error || '\u2014'}</span>
                <span className="ml-auto text-xs text-white/40">{e.environment} &middot; {when(e.received_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="mt-6 flex items-start gap-2 text-xs text-white/35">
        <Anchor className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        FareHarbor sends duplicates by design, so a booking is stored once per booking UUID and the newest payload
        wins. The receiver always answers 200 - if it ever returned an error too often FareHarbor would switch the
        webhook off.
      </p>
    </div>
  );
};

export default FareHarborWebhooks;
